import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';

// Same light as the "in competition" pill of the app (NeonBorder of the app): the colors of the
// ranks (soldier to legend), fading at both ends, 2.8 times the height of the box, one turn in
// 1.8 s at a constant speed, along the border and its curves.
const COLORS: [number, number, number, number][] = [
  [205, 127, 50, 0.9],
  [192, 192, 192, 1],
  [255, 215, 0, 1],
  [125, 211, 252, 1],
  [125, 211, 252, 0.6],
];
const LIGHT_SIZE = 2.8;
const DURATION_S = 1.8;
// Pieces of the light (each one a solid color): enough for a smooth gradient.
const PIECES = 16;
// Unit of the path: the dashes are in thousandths of the border.
const PATH_LENGTH = 1000;

const run = keyframes`
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: -${PATH_LENGTH}; }
`;

// The color of the light at t (0 = its tail, 1 = its head): transparent, the colors, transparent.
const sampleLight = (t: number) => {
  const first = COLORS[0];
  const last = COLORS[COLORS.length - 1];
  const stops = [[first[0], first[1], first[2], 0], ...COLORS, [last[0], last[1], last[2], 0]];
  const position = Math.min(Math.max(t, 0), 1) * (stops.length - 1);
  const index = Math.min(Math.floor(position), stops.length - 2);
  const local = position - index;
  const [r, g, b, a] = [0, 1, 2, 3].map((i) => (
    stops[index][i] + (stops[index + 1][i] - stops[index][i]) * local
  ));
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a.toFixed(3)})`;
};

interface NeonBorderProps {
  // Width of the border of the box, the light is drawn on it.
  borderWidth: number;
}

// A light that runs around the border of its parent (a positioned box with a fully rounded border,
// like a pill). The SVG dashes follow the rounded border at a constant speed: one dash per piece of
// the light, each one a bit behind the previous, so the light keeps its gradient and its length.
const NeonBorder: React.FC<NeonBorderProps> = React.memo((props) => {
  const { borderWidth } = props;
  const ref = useRef<SVGSVGElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize((prev) => (
        prev.width === width && prev.height === height ? prev : { width, height }
      ));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const pieces = useMemo(() => {
    const { width, height } = size;
    if (!width || !height) {
      return [];
    }
    // The border line runs in the middle of the border.
    const w = width - borderWidth;
    const h = height - borderWidth;
    const r = Math.min(w, h) / 2;
    const perimeter = 2 * (w - 2 * r) + 2 * (h - 2 * r) + 2 * Math.PI * r;
    const piece = ((height * LIGHT_SIZE) / PIECES / perimeter) * PATH_LENGTH;
    return Array.from({ length: PIECES }, (_v, i) => ({
      color: sampleLight(1 - (i + 0.5) / PIECES),
      // A bit longer than the step, so the pieces overlap.
      dash: piece * 1.12,
      // Piece i is i steps behind the head: the same animation, started a bit later.
      delay: -DURATION_S + ((i * piece) / PATH_LENGTH) * DURATION_S,
    }));
  }, [borderWidth, size]);

  const half = borderWidth / 2;
  const w = Math.max(size.width - borderWidth, 0);
  const h = Math.max(size.height - borderWidth, 0);

  return (
    <Box
      component="svg"
      ref={ref}
      aria-hidden
      sx={{
        position: 'absolute',
        inset: `-${borderWidth}px`,
        width: `calc(100% + ${borderWidth * 2}px)`,
        height: `calc(100% + ${borderWidth * 2}px)`,
        overflow: 'visible',
        pointerEvents: 'none',
      }}
    >
      {pieces.map((p, i) => (
        <Box
          component="rect"
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          x={half}
          y={half}
          width={w}
          height={h}
          rx={h / 2}
          ry={h / 2}
          pathLength={PATH_LENGTH}
          fill="none"
          stroke={p.color}
          strokeWidth={borderWidth}
          strokeDasharray={`${p.dash} ${PATH_LENGTH - p.dash}`}
          sx={{
            animation: `${run} ${DURATION_S}s linear infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </Box>
  );
});

export default NeonBorder;
